import { useCallback, useEffect, useRef, useState } from 'react'

const COOLDOWN_DURATION = 10000 // 10 seconds in milliseconds

interface RefetchQuoteState {
  isOnCooldown: boolean
  isFetching: boolean
  remainingTime: number
  remainingMs: number
  canRefetch: boolean
}

/**
 * Hook to manage refetch quote functionality with cooldown timer
 * @param refetchQuote - The refetch function from RTK Query
 * @param isLoading - Whether a request is currently in progress
 * @returns state and methods to control refetch
 */
export function useRefetchQuote(
  refetchQuote: (() => void) | undefined,
  isLoading: boolean
): {
  handleRefetch: () => void
  state: RefetchQuoteState
} {
  const [cooldownEnd, setCooldownEnd] = useState<number | null>(null)
  const [remainingTime, setRemainingTime] = useState(0)
  const [remainingMs, setRemainingMs] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const lastLoadingState = useRef(isLoading)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Track when loading state changes (auto-refetch or manual refetch completed)
  useEffect(() => {
    // If loading just started
    if (isLoading && !lastLoadingState.current) {
      setIsFetching(true)
    }

    // If loading just finished
    if (!isLoading && lastLoadingState.current) {
      setIsFetching(false)
      // Start cooldown when request completes
      const endTime = Date.now() + COOLDOWN_DURATION
      setCooldownEnd(endTime)
    }

    lastLoadingState.current = isLoading
  }, [isLoading])

  // Cooldown timer - smooth progress updates
  useEffect(() => {
    if (cooldownEnd === null) {
      setRemainingTime(0)
      setRemainingMs(0)
      return
    }

    const updateRemainingTime = () => {
      const now = Date.now()
      const remMs = Math.max(0, cooldownEnd - now)
      const remSec = Math.ceil(remMs / 1000)
      setRemainingTime(remSec)
      setRemainingMs(remMs)

      if (remMs <= 0) {
        setCooldownEnd(null)
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
    }

    updateRemainingTime()
    timerRef.current = setInterval(updateRemainingTime, 50) // 50ms for smoother visual updates

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [cooldownEnd])

  const handleRefetch = useCallback(() => {
    if (!refetchQuote) return
    if (isFetching) return
    if (cooldownEnd && Date.now() < cooldownEnd) return

    refetchQuote()
    // Cooldown will be set by the isLoading effect
  }, [refetchQuote, isFetching, cooldownEnd])

  const isOnCooldown = cooldownEnd !== null && Date.now() < cooldownEnd
  const canRefetch = !isFetching && !isOnCooldown && !!refetchQuote

  return {
    handleRefetch,
    state: {
      isOnCooldown,
      isFetching,
      remainingTime,
      remainingMs,
      canRefetch,
    },
  }
}
